/**
 * Top app bar. On the unauthenticated home page renders a centered logo on a brand background.
 * Everywhere else: hamburger (mobile only), logo, and LanguageSelector.
 */
import { Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { useUiStore } from '@/store/ui.store'
import logoLight from '@/assets/logo/olimhub-logo.png'
import logoDark from '@/assets/logo/olimhub-logo-dark.png'
import { LanguageSelector } from './LanguageSelector'

interface HeaderProps {
  onMenuOpen: () => void
}

export function Header({ onMenuOpen }: HeaderProps) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const { theme } = useUiStore()
  const location = useLocation()

  const isUnauthHome = !isAuthenticated && location.pathname === '/'

  const logo = theme === 'dark' ? logoDark : logoLight

  if (isUnauthHome) {
    return (
      <header
        style={{
          height: 'var(--header-main)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 60,
          background: 'color-mix(in srgb, var(--brand) 17%, var(--bg))',
        }}
      >
        <Link to="/">
          <img src={logo} alt="OlimHub" style={{ height: 24, width: 'auto' }} />
        </Link>
      </header>
    )
  }

  return (
    <header
      style={{
        height: 'var(--header-main)',
        flexShrink: 0,
        paddingInline: 6,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 60,
      }}
    >
      {isAuthenticated ? (
        <button
          className="lg:hidden flex items-center justify-center"
          aria-label={t('common.open_menu')}
          onClick={onMenuOpen}
          style={{
            width: 44,
            height: 44,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
          }}
        >
          <Menu size={24} />
        </button>
      ) : (
        <div className="lg:hidden" style={{ width: 44, height: 44, flexShrink: 0 }} />
      )}

      <Link to="/" style={{ marginInlineStart: 8 }}>
        <img src={logo} alt="OlimHub" style={{ height: 24, width: 'auto' }} />
      </Link>

      <LanguageSelector />
    </header>
  )
}
